/**
 * The Problems panel (W2-12a checker-runtime; DESIGN.md §4.18, §5.10): a dock tab listing what the checker found.
 *
 *  - Filters: severity chips with counts (Fatal / Error / Warning / Tip), category chips (Phira / Gameplay / Style),
 *    scope (whole chart or the active line) and a search over the rule id, WHAT, WHY and fix labels. Severity,
 *    category and scope persist (`ext.checker.filter`) and also steer next / previous problem.
 *  - Rows: severity icon and word (colour is never the only signal), WHAT, WHY (under the current problem only, so
 *    the list stays compact; under every row with `ext.checker.explainAll`; always in the row's tooltip and the
 *    search), where (line and beat), and the actions: Go (`nav.reveal`), the first fix as a button, further fixes in
 *    a menu, and Ignore here (stored in the chart, undoable). Fixes that only do what Go does are not offered (the
 *    checker leaves them out, core/check/geometry.ts `duplicatesGo`). In a dock narrower than 680 px a row stacks:
 *    the message on top, then the place and the actions (UX-093). Clicking a row goes there; Up / Down / Home / End
 *    move between rows, Enter goes; the focused row is the current problem (Ctrl+. fixes it).
 *  - Level (DESIGN.md §2.4 "Problems shown"): in Guided, fatal problems, errors and warnings are listed and the
 *    tips are folded into one "Show N tips" row at the end (opened on demand, and at once when the current problem
 *    is a tip, e.g. after Alt+] or a click on a tip tick); Full lists everything.
 *  - The list is virtualised (virtualRows.ts): only the rows in view, plus the current problem and the row with the
 *    keyboard focus, are rendered, with measured row heights, so thousands of problems stay cheap and revealing one
 *    deep in the list renders a few rows. Up / Down / Home / End / Page Up / Page Down move the focus over the whole
 *    list (rendering the target first).
 *  - A fatal banner (quick export is blocked), the checking status ("Checking…", last pass facts), a notice when a
 *    check could not run (its earlier results are kept), "Check now", empty states (no chart, no problems,
 *    everything filtered out) and the problems ignored in this chart with "Stop ignoring".
 * Works over any ProblemsService; the checker's own service adds persisted filters, the current problem, the
 * checking status and the "fixed, checking again" state.
 */

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react';
import type { IconName, PanelProps } from '../../app/registry/types';
import type { CheckCategory, FixRef, Problem, Severity } from '../../core/check/types';
import { Button, EmptyState, Icon, IconButton, Menu, Section, Segmented, TextField } from '../../ui';
import type { MenuItem } from '../../ui/types';
import { m } from './checker.messages';
import {
  useCheckerText,
  useEditorValue,
  useOpenStore,
  useProblemsVersion,
  useSettingsValue,
  useStoreVersion,
} from './hooks';
import {
  ALL_CATEGORIES,
  ALL_SEVERITIES,
  DEFAULT_FILTER,
  canGo,
  filterProblems,
  ignoreProblemInDoc,
  locationText,
  revealArgs,
  revealProblem,
  ruleOfKey,
  searchText,
  tempoOf,
  unignoreInDoc,
  type ProblemFilter,
} from './navigation';
import s from './ProblemsPanel.module.css';
import { asCheckerService } from './problemsService';
import { ROW_KEY_ATTR, useVirtualRows } from './virtualRows';

/** "A, B and C" in the locale's words (plain commas where the runtime lacks Intl.ListFormat). */
function joinNames(locale: string, items: readonly string[]): string {
  try {
    return new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(items);
  } catch {
    return items.join(', ');
  }
}

export const SEVERITY_ICON: Readonly<Record<Severity, IconName>> = {
  fatal: 'OctagonX',
  error: 'CircleX',
  warning: 'TriangleAlert',
  tip: 'Lightbulb',
};

const CATEGORY_ICON: Readonly<Record<CheckCategory, IconName>> = {
  phira: 'Smartphone',
  gameplay: 'Gamepad2',
  style: 'Sparkles',
};

/** User preference (Settings > Panels and tools): show the WHY line under every problem, not only the current one. */
export const EXPLAIN_ALL_KEY = 'checker.explainAll';

/** Key of the "Show N tips" row (problem ids are `${rule}:${key}`, so it never collides). */
export const TIPS_ROW_KEY = '#tips';

/** One row of the list: a problem, or the folded tips of the Guided level. */
type Entry =
  | { readonly kind: 'problem'; readonly key: string; readonly p: Problem; readonly pos: number }
  | { readonly kind: 'tips'; readonly key: string; readonly count: number; readonly open: boolean };

/** The rows of the list: everything, or in Guided the non-tips, then the tips row, then the tips when open. */
export function listEntries(shown: readonly Problem[], foldTips: boolean, tipsOpen: boolean): Entry[] {
  const row = (p: Problem, pos: number): Entry => ({ kind: 'problem', key: p.id, p, pos });
  if (!foldTips) return shown.map(row);
  const out: Entry[] = [];
  const tips: Array<readonly [Problem, number]> = [];
  shown.forEach((p, i) => {
    if (p.severity === 'tip') tips.push([p, i]);
    else out.push(row(p, i));
  });
  if (tips.length === 0) return out;
  out.push({ kind: 'tips', key: TIPS_ROW_KEY, count: tips.length, open: tipsOpen });
  if (tipsOpen) for (const [p, i] of tips) out.push(row(p, i));
  return out;
}

function scrollIntoViewSafe(el: HTMLElement): void {
  try {
    el.scrollIntoView({ block: 'nearest' });
  } catch {
    // Environments without layout (tests) have no scrollIntoView.
  }
}

function keepFocus(e: ReactMouseEvent): void {
  if (e.button !== 0) return;
  const active = document.activeElement;
  if (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement) return;
  e.preventDefault();
}

function cx(...names: ReadonlyArray<string | false | null | undefined>): string {
  return names.filter((n): n is string => typeof n === 'string' && n !== '').join(' ');
}

function toggle<T>(list: readonly T[], v: T): T[] {
  return list.includes(v) ? list.filter((x) => x !== v) : [...list, v];
}

/** Fixes worth a button: the ones that do more than "Go" already does. */
export function fixesBesidesGo(p: Problem, goArgs: unknown): FixRef[] {
  const go = goArgs === null ? null : JSON.stringify(goArgs);
  return p.fixes.filter((f) => !(f.command === 'nav.reveal' && go !== null && JSON.stringify(f.args) === go));
}

export function ProblemsPanel(props: PanelProps) {
  const { services } = props;
  const text = useCheckerText(services);
  const { t, tr } = text;
  useProblemsVersion(services);
  const store = useOpenStore(services);
  const storeVersion = useStoreVersion(store);
  const settings = useSettingsValue(services);
  const activeLine = useEditorValue(services, (st) => st.activeLine);
  const checker = asCheckerService(services.problems);

  const [localFilter, setLocalFilter] = useState<ProblemFilter>(DEFAULT_FILTER);
  const [query, setQuery] = useState('');
  const [localCurrent, setLocalCurrent] = useState<string | null>(null);
  const [tipsOpen, setTipsOpen] = useState(false);
  /** Row that holds (or last held) the keyboard focus. */
  const [focusKey, setFocusKey] = useState<string | null>(null);
  /** Row to focus once it is rendered (keyboard moves). */
  const pendingFocus = useRef<string | null>(null);
  /** Focusable element of each rendered row (the row button, or the tips toggle). */
  const rowEls = useRef(new Map<string, HTMLButtonElement>());
  const scrollRef = useRef<HTMLDivElement>(null);

  const filter: ProblemFilter = { ...(checker?.filter() ?? localFilter), query };
  const setFilter = (patch: Partial<Omit<ProblemFilter, 'query'>>): void => {
    if (checker !== null) checker.setFilter(patch);
    else setLocalFilter((f) => ({ ...f, ...patch }));
  };
  const current = checker?.current() ?? localCurrent;
  const setCurrent = (id: string | null): void => {
    if (checker !== null) checker.setCurrent(id);
    else setLocalCurrent(id);
  };
  const explainAll = settings.ext[EXPLAIN_ALL_KEY] === true;
  const beatStyle = settings.editing.beatDisplay;
  const guided = settings.general.level === 'guided';

  const all = services.problems.problems();
  const counts = services.problems.counts();
  // A new view object whenever the document changed (line names, ignore list, tempo), so memos below follow it.
  const view = useMemo(
    () => (store === null ? null : { doc: store.doc, idx: store.idx, version: storeVersion }),
    [store, storeVersion],
  );
  const doc = view?.doc ?? null;
  const tempo = tempoOf(services, view);
  // Search text is built only for a non-empty query (matchesQuery returns early otherwise).
  const textOf = (p: Problem): string =>
    `${searchText(p, tr)} ${locationText(p, view?.doc ?? null, view?.idx ?? null, tempo, beatStyle, t)}`;
  const shown = filterProblems(all, filter, { activeLine, doc, text: textOf });

  const status = checker?.status() ?? null;
  const resolving = checker?.resolving() ?? new Set<string>();
  const ignored = store?.doc.editor.ignoredProblems ?? [];

  // Guided folds the tips; the current problem being a tip (next / previous, a tick) opens them, and they stay open
  // until the user folds them again.
  const currentIsTip = current !== null && shown.some((p) => p.id === current && p.severity === 'tip');
  if (guided && currentIsTip && !tipsOpen) setTipsOpen(true);
  const entries = listEntries(shown, guided, tipsOpen || currentIsTip);
  const keys = entries.map((e) => e.key);
  const indexOfKey = (key: string | null): number => (key === null ? -1 : keys.indexOf(key));
  const currentIndex = indexOfKey(current);
  const focusIndex = indexOfKey(focusKey);
  const tabStop = currentIndex >= 0 ? current : focusIndex >= 0 ? focusKey : (keys[0] ?? null);
  const rows = useVirtualRows({ keys, scrollRef, forced: [currentIndex, focusIndex, indexOfKey(tabStop)] });

  // The current problem is always rendered and scrolled into view (next / previous, ticks, field badges).
  useEffect(() => {
    if (current === null) return;
    const el = rowEls.current.get(current);
    if (el !== undefined) scrollIntoViewSafe(el);
  }, [current]);

  // A keyboard move focuses its target once the target is rendered.
  useEffect(() => {
    const key = pendingFocus.current;
    if (key === null) return;
    const el = rowEls.current.get(key);
    if (el === undefined) return;
    pendingFocus.current = null;
    el.focus({ preventScroll: true });
    scrollIntoViewSafe(el);
  });

  const go = (p: Problem): void => {
    setCurrent(p.id);
    if (checker !== null) void checker.reveal(p.id);
    else void revealProblem(services, p);
  };
  const fix = (p: Problem, index: number): void => {
    setCurrent(p.id);
    void services.problems.applyFix(p.id, index);
  };
  const ignore = (p: Problem): void => {
    if (checker !== null) checker.ignore(p.id);
    else if (store !== null && ignoreProblemInDoc(store, p))
      services.ui.toast({ key: m.toastIgnored }, { kind: 'info', undo: true });
  };
  const unignore = (keysToShow: readonly string[]): void => {
    if (checker !== null) checker.unignore(keysToShow);
    else if (store !== null) unignoreInDoc(store, keysToShow);
  };
  const toggleTips = (open: boolean): void => {
    setTipsOpen(open);
    // Folding the tips away from a current tip: the tip is no longer where the user is.
    if (!open && currentIsTip) setCurrent(null);
  };

  const moveFocus = (index: number): void => {
    const e = entries[Math.max(0, Math.min(entries.length - 1, index))];
    if (e === undefined) return;
    pendingFocus.current = e.key;
    setFocusKey(e.key);
    if (e.kind === 'problem') setCurrent(e.p.id);
  };
  const onRowKey = (e: ReactKeyboardEvent, index: number): void => {
    if (e.altKey || e.ctrlKey || e.metaKey) return;
    let next: number | null = null;
    if (e.key === 'ArrowDown') next = index + 1;
    else if (e.key === 'ArrowUp') next = index - 1;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = entries.length - 1;
    else if (e.key === 'PageDown') next = index + rows.pageRows;
    else if (e.key === 'PageUp') next = index - rows.pageRows;
    if (next === null) return;
    e.preventDefault();
    moveFocus(next);
  };
  const focusRef =
    (key: string) =>
    (el: HTMLButtonElement | null): void => {
      if (el === null) rowEls.current.delete(key);
      else rowEls.current.set(key, el);
    };

  const statusText =
    status === null || !status.connected
      ? null
      : status.workerFailed
        ? t(m.workerStopped)
        : status.checking
          ? t(status.checkingPhira ? m.checking : m.checkingRest)
          : status.stats !== null
            ? t(m.checkedStats, {
                notes: status.stats.notes,
                lines: status.stats.lines,
                ms: Math.round(status.stats.checkMs),
              })
            : t(m.notChecked);
  const failedRules = status?.failedRules ?? [];

  const rules = services.problems.rules();
  const ruleTitle = (id: string): string | null => {
    const r = rules.find((x) => x.id === id);
    return r === undefined ? null : tr(r.title);
  };
  /** "PH-07 · Effect Phira rejects": a rule as the ignored list and the failed-checks notice name it. */
  const ruleName = (id: string): string => {
    const title = ruleTitle(id);
    return title === null ? t(m.ignoredUnknown, { rule: id }) : t(m.ignoredItem, { rule: id, title });
  };

  const problemRow = (e: Extract<Entry, { kind: 'problem' }>, index: number): ReactNode => {
    const p = e.p;
    const goArgs = revealArgs(p, tempo, doc);
    const fixes = fixesBesidesGo(p, goArgs);
    const primary = fixes[0];
    const more = fixes.slice(1);
    const isCurrent = p.id === current;
    const showWhy = explainAll || isCurrent;
    const isResolving = resolving.has(p.id);
    const message = tr(p.message);
    const where = locationText(p, doc, store?.idx ?? null, tempo, beatStyle, t);
    const severity = t(m.severityName, { severity: p.severity });
    const moreItems: MenuItem[] = more.map((f) => ({
      kind: 'action',
      label: tr(f.label),
      icon: f.command === 'nav.reveal' ? 'Crosshair' : 'Wrench',
      onSelect: () => {
        fix(p, p.fixes.indexOf(f));
      },
    }));
    return (
      <li
        key={e.key}
        ref={rows.measure}
        {...{ [ROW_KEY_ATTR]: e.key }}
        className={cx(s.row, s.vrow, isCurrent && s.current, isResolving && s.resolving)}
        style={{ top: rows.offsets[index] ?? 0 }}
        aria-setsize={shown.length}
        aria-posinset={e.pos + 1}
        data-severity={p.severity}
        data-problem={p.id}
      >
        <button
          type="button"
          className={s.rowMain}
          ref={focusRef(e.key)}
          tabIndex={e.key === tabStop ? 0 : -1}
          aria-current={isCurrent ? 'true' : undefined}
          aria-label={t(m.rowLabel, { severity, message })}
          title={!showWhy && p.why !== undefined ? tr(p.why) : undefined}
          onFocus={() => {
            setFocusKey(e.key);
            setCurrent(p.id);
          }}
          onKeyDown={(ev) => {
            onRowKey(ev, index);
          }}
          onClick={() => {
            go(p);
          }}
        >
          <span className={s.sev} data-severity={p.severity}>
            <Icon name={SEVERITY_ICON[p.severity]} size={16} />
            <span className={s.sevWord}>{severity}</span>
          </span>
          <span className={s.text}>
            <span className={s.message}>{message}</span>
            {showWhy && p.why !== undefined && <span className={s.why}>{tr(p.why)}</span>}
            {isResolving && <span className={s.fixing}>{t(m.fixingNote)}</span>}
          </span>
          <span className={s.where}>
            <span className={s.rule}>{p.rule}</span>
            <span>{where}</span>
          </span>
        </button>
        <div className={s.actions}>
          <Button
            size="sm"
            variant="ghost"
            icon="Crosshair"
            disabledReason={canGo(p) ? null : { key: m.goNowhere }}
            aria-label={t(m.goLabel)}
            onClick={() => {
              go(p);
            }}
          >
            {t(m.go)}
          </Button>
          {primary !== undefined && (
            <Button
              size="sm"
              variant="secondary"
              icon={primary.command === 'nav.reveal' ? 'Crosshair' : 'Wrench'}
              onClick={() => {
                fix(p, p.fixes.indexOf(primary));
              }}
            >
              {tr(primary.label)}
            </Button>
          )}
          {moreItems.length > 0 && (
            <Menu
              label={t(m.fixesOf)}
              align="end"
              trigger={<IconButton icon="ChevronDown" size="sm" label={{ key: m.moreFixes }} />}
              items={moreItems}
            />
          )}
          <IconButton
            icon="EyeOff"
            size="sm"
            label={{ key: m.ignoreLabel }}
            onClick={() => {
              ignore(p);
            }}
          />
        </div>
      </li>
    );
  };

  const tipsRow = (e: Extract<Entry, { kind: 'tips' }>, index: number): ReactNode => (
    <li
      key={e.key}
      ref={rows.measure}
      {...{ [ROW_KEY_ATTR]: e.key }}
      className={cx(s.tipsRow, s.vrow)}
      style={{ top: rows.offsets[index] ?? 0 }}
      data-tips-row=""
    >
      <button
        type="button"
        className={s.tipsToggle}
        ref={focusRef(e.key)}
        tabIndex={e.key === tabStop ? 0 : -1}
        aria-expanded={e.open}
        onFocus={() => {
          setFocusKey(e.key);
        }}
        onKeyDown={(ev) => {
          onRowKey(ev, index);
        }}
        onClick={() => {
          toggleTips(!e.open);
        }}
      >
        <Icon name={e.open ? 'ChevronDown' : 'ChevronRight'} size={14} />
        <span className={s.sev} data-severity="tip">
          <Icon name={SEVERITY_ICON.tip} size={16} />
        </span>
        <span className={s.tipsLabel}>{t(e.open ? m.tipsHide : m.tipsShow, { count: e.count })}</span>
        <span className={s.tipsHint}>{t(m.tipsFoldedHint)}</span>
      </button>
    </li>
  );

  return (
    <div className={s.panel} data-region="problems" role="region" aria-label={t(m.panelTitle)}>
      <div className={s.toolbar} role="toolbar" aria-label={t(m.filtersLabel)}>
        <div className={s.group}>
          {ALL_SEVERITIES.map((sev) => {
            const on = filter.severities.includes(sev);
            return (
              <button
                key={sev}
                type="button"
                className={cx(s.chip, on && s.chipOn)}
                data-severity={sev}
                aria-pressed={on}
                aria-label={t(m.severityFilterLabel, { severity: sev, count: counts[sev] })}
                onMouseDown={keepFocus}
                onClick={() => {
                  setFilter({ severities: toggle(filter.severities, sev) });
                }}
              >
                <Icon name={SEVERITY_ICON[sev]} size={14} />
                <span>{t(m.severityFilter, { severity: sev, count: counts[sev] })}</span>
              </button>
            );
          })}
        </div>
        <div className={s.group}>
          {ALL_CATEGORIES.map((cat) => {
            const on = filter.categories.includes(cat);
            return (
              <button
                key={cat}
                type="button"
                className={cx(s.chip, on && s.chipOn)}
                aria-pressed={on}
                aria-label={t(m.categoryFilterLabel, { category: cat })}
                onMouseDown={keepFocus}
                onClick={() => {
                  setFilter({ categories: toggle(filter.categories, cat) });
                }}
              >
                <Icon name={CATEGORY_ICON[cat]} size={14} />
                <span>{t(m.categoryName, { category: cat })}</span>
              </button>
            );
          })}
        </div>
        <Segmented<'chart' | 'line'>
          size="sm"
          ariaLabel={t(m.scopeLabel)}
          value={filter.scope}
          options={[
            { value: 'chart', label: t(m.scopeChart), icon: 'Layers' },
            {
              value: 'line',
              label: t(m.scopeLine),
              icon: 'Minus',
              ...(activeLine === null ? { disabledReason: { key: m.scopeLineNone } } : {}),
            },
          ]}
          onChange={(scope) => {
            setFilter({ scope });
          }}
        />
        <TextField
          className={s.search}
          label={t(m.searchLabel)}
          hideLabel
          type="search"
          value={query}
          placeholder={{ key: m.searchPlaceholder }}
          onChange={(v) => {
            setQuery(v);
          }}
          onCommit={(v) => {
            setQuery(v);
          }}
        />
        <div className={s.statusGroup}>
          {statusText !== null && (
            <span className={s.status} aria-live="polite">
              {status?.checking === true && <Icon name="LoaderCircle" size={14} />}
              {statusText}
            </span>
          )}
          <IconButton
            icon="RefreshCw"
            size="sm"
            label={{ key: m.checkNow }}
            command="problems.checkNow"
            onClick={() => {
              services.problems.fullCheck();
            }}
            disabledReason={store === null ? { key: m.reasonNoProject } : null}
          />
        </div>
      </div>

      {counts.fatal > 0 && (
        <div className={s.banner} role="alert">
          <Icon name="OctagonX" size={16} />
          <span>{t(m.fatalBanner, { count: counts.fatal })}</span>
        </div>
      )}

      {store !== null && failedRules.length > 0 && (
        <div className={s.notice} role="status">
          <Icon name="TriangleAlert" size={14} />
          <span>
            {t(m.failedRules, {
              count: failedRules.length,
              rules: joinNames(text.locale, failedRules.map(ruleName)),
            })}
          </span>
        </div>
      )}

      <div className={s.body}>
        {store === null ? (
          <div className={s.state}>
            <EmptyState icon="FileQuestion" title={{ key: m.noProjectTitle }} body={{ key: m.noProjectBody }} />
          </div>
        ) : all.length === 0 && status !== null && status.checking && status.stats === null ? (
          <div className={s.state}>
            <EmptyState icon="LoaderCircle" title={{ key: m.checkingTitle }} body={{ key: m.checkingBody }} />
          </div>
        ) : all.length === 0 ? (
          <div className={s.state}>
            <EmptyState icon="CircleCheck" title={{ key: m.noProblemsTitle }} body={{ key: m.noProblemsBody }} />
          </div>
        ) : shown.length === 0 ? (
          <div className={cx(s.state, s.noMatch)} role="status">
            <Icon name="Filter" size={24} />
            <p className={s.noMatchTitle}>{t(m.noMatchTitle)}</p>
            <p className={s.noMatchBody}>{t(m.noMatchBody)}</p>
            <Button
              size="sm"
              icon="FilterX"
              onClick={() => {
                setFilter({ severities: ALL_SEVERITIES, categories: ALL_CATEGORIES, scope: 'chart' });
                setQuery('');
              }}
            >
              {t(m.showAll)}
            </Button>
          </div>
        ) : (
          <p className={s.count}>{t(m.shownCount, { shown: shown.length, total: all.length })}</p>
        )}

        <div className={s.scroller} ref={scrollRef} hidden={store === null || shown.length === 0}>
          <ul className={s.list} aria-label={t(m.listLabel)} style={{ height: rows.total }}>
            {store !== null &&
              rows.indices.map((i) => {
                const e = entries[i];
                if (e === undefined) return null;
                return e.kind === 'problem' ? problemRow(e, i) : tipsRow(e, i);
              })}
          </ul>
        </div>

        {store !== null && ignored.length > 0 && (
          <Section
            className={s.ignored}
            collapsible
            defaultOpen={false}
            level={4}
            title={t(m.ignoredTitle, { count: ignored.length })}
            actions={
              <Button
                size="sm"
                variant="ghost"
                icon="Eye"
                onClick={() => {
                  unignore(ignored);
                }}
              >
                {t(m.stopIgnoringAll)}
              </Button>
            }
          >
            <ul className={s.ignoredList}>
              {ignored.map((key) => {
                const rule = ruleOfKey(key);
                return (
                  <li key={key} className={s.ignoredRow}>
                    <span className={s.ignoredText}>
                      {ruleName(rule)}
                      <span className={s.ignoredKey}>{key.slice(rule.length + 1)}</span>
                    </span>
                    <Button
                      size="sm"
                      variant="ghost"
                      icon="Eye"
                      onClick={() => {
                        unignore([key]);
                      }}
                    >
                      {t(m.stopIgnoring)}
                    </Button>
                  </li>
                );
              })}
            </ul>
          </Section>
        )}
      </div>
    </div>
  );
}
